import { useNavigate } from 'react-router-dom';
import { ArrowRight, Compass, Heart, MessageCircle, Route } from 'lucide-react';
import { useLang } from '../i18n';
import { Reveal } from './Reveal';
import { SectionHeader } from './SectionHeader';

const STEPS = [
  { icon: Compass, keys: 'hiw.s1' },
  { icon: Route, keys: 'hiw.s2' },
  { icon: MessageCircle, keys: 'hiw.s3' },
  { icon: Heart, keys: 'hiw.s4' },
];

export const HowItWorks = () => {
  const navigate = useNavigate();
  const { t } = useLang();

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="scroll-mt-20 bg-white py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <SectionHeader
          align="center"
          eyebrow={t('hiw.kicker')}
          title={t('hiw.title')}
          text={t('hiw.text')}
        />

        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.keys} delay={i * 0.12} className="group text-center">
              <div className="relative mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-eco-cream transition-colors duration-500 group-hover:bg-eco-green">
                <s.icon size={24} className="text-eco-gold" />
                <span className="absolute -top-2 -right-2 font-serif text-xs font-semibold text-eco-gold">
                  0{i + 1}
                </span>
              </div>
              <h3 className="eco-card-title mt-5">{t(`${s.keys}.t`)}</h3>
              <p className="eco-secondary mt-2 text-eco-ink/60">{t(`${s.keys}.d`)}</p>
            </Reveal>
          ))}
        </div>

        <div id="planeamento" className="mt-24 scroll-mt-28">
          <Reveal className="text-center">
            <h3 className="text-2xl leading-[1.2] font-semibold tracking-[-0.02em] text-eco-ink">{t('hiw.start.t')}</h3>
          </Reveal>
          <div className="mx-auto mt-10 grid max-w-4xl gap-6 md:grid-cols-2">
            <Reveal delay={0.1}>
              <div className="group flex h-full flex-col rounded-2xl border border-eco-ink/10 bg-eco-cream p-7 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg">
                <h4 className="eco-card-title">{t('hiw.exp.t')}</h4>
                <p className="eco-secondary mt-3 flex-1 text-eco-ink/60">
                  {t('hiw.exp.d')}
                </p>
                <button
                  data-testid="start-explore-button"
                  onClick={() => navigate('/experiences')}
                  className="eco-btn-text group mt-3 self-start"
                >
                  {t('hiw.exp.c')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="group flex h-full flex-col rounded-2xl bg-eco-green p-7 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg">
                <h4 className="text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-white">{t('hiw.plan.t')}</h4>
                <p className="eco-secondary mt-3 flex-1 text-white/65">
                  {t('hiw.plan.d')}
                </p>
                <button
                  data-testid="start-plan-button"
                  onClick={() => navigate('/plan-my-trip')}
                  className="mt-3 inline-flex min-h-11 items-center gap-2 self-start text-sm font-semibold text-eco-gold transition-colors hover:text-white"
                >
                  {t('hiw.plan.c')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
